import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Radar } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { OffsetPager } from '@/shared/ui/CursorPager'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import { listSubjects, type Subject } from '@/features/dossier/api'
import { LabeledBar, fmtRelative } from '@/features/dossier/components/primitives'
import { listCoordination, scanCoordination, standDownSignal, type Coordination } from './api'

/**
 * PART IV.1 — inauthentic coordination.
 *
 * ## What this page claims, and what it refuses to claim
 *
 * It claims: this exact string was posted by N different accounts inside a window. That is a
 * measurable fact about text. It does NOT claim the accounts are bots, are paid, are the same
 * person, or are acting in bad faith — a slogan repeats because slogans are repeated, and
 * genuine enthusiasm looks identical to a script from the outside. Confidence is therefore
 * capped at 90 server-side and the phrase is quoted verbatim rather than characterised, so the
 * operator judges the evidence instead of the label.
 *
 * ## Stand-down is a first-class action
 *
 * A signal that turns out to be a real trending phrase should be closed with a reason, not left
 * to decay. That reason is what stops the same phrase being re-escalated next week by somebody
 * who was not here.
 */
export function CoordinationPage() {
  const authorities = useSession((s) => s.authorities)
  const canTriage = hasPermission(authorities, 'warroom:triage')

  const [signals, setSignals] = useState<Coordination[] | null>(null)
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [subjectId, setSubjectId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [query, setQuery] = useState('')
  const [draftQuery, setDraftQuery] = useState('')
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState({ pages: 1, elements: 0 })

  const load = useCallback(async () => {
    try {
      setError(null)
      const found = await listCoordination({ q: query || undefined, page, size: 50 })
      setSignals(found.content)
      setTotal({ pages: found.totalPages, elements: found.totalElements })
    } catch (err) {
      setError(explain(err))
    }
  }, [query, page])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    void listSubjects({ size: '100' }).then((rows) => {
      setSubjects(rows)
      setSubjectId((c) => c || rows[0]?.id || '')
    }).catch(() => setSubjects([]))
  }, [])

  if (error && !signals) return <ErrorStrip message={error} />
  if (!signals) return <LoadingBlock label="Loading signals" />

  const live = signals.filter((s) => s.stoodDownAt == null)
  const closed = signals.filter((s) => s.stoodDownAt != null)

  async function run(work: () => Promise<unknown>, message?: string) {
    setBusy(true); setError(null); setNotice(null)
    try {
      await work()
      if (message) setNotice(message)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Coordination"
        description="Where the same words are appearing from many accounts at once. This counts
                     strings — it does not read minds, and it never names a person."
      />

      {error && <ErrorStrip message={error} />}

      <SearchBar draft={draftQuery} onDraft={setDraftQuery} query={query}
                 onSearch={() => { setPage(0); setQuery(draftQuery) }}
                 onClear={() => { setDraftQuery(''); setQuery(''); setPage(0) }}
                 total={total.elements} unit="signals" />
      {notice && (
        <p className="rounded border border-emerald-500/30 bg-emerald-500/5 px-3 py-2
                      text-[11px] text-emerald-700 dark:text-emerald-400">{notice}</p>
      )}

      {canTriage && (
        <Panel>
          <div className="flex flex-wrap items-end gap-2 p-3">
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Scan the recent threads about
              </span>
              <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}
                      className="h-7 rounded border border-input bg-background px-2
                                 text-[12px]">
                {subjects.map((s) => <option key={s.id} value={s.id}>{s.displayName}</option>)}
              </select>
            </label>
            <button type="button" disabled={busy || !subjectId}
                    onClick={() => void run(async () => {
                      const found = await scanCoordination(subjectId)
                      setNotice(found.length === 0
                        ? 'Nothing repeated often enough to be worth flagging.'
                        : `${found.length} repeated phrase${found.length === 1 ? '' : 's'} found.`)
                    })}
                    className="inline-flex items-center gap-1 rounded border border-border
                               px-3 py-1.5 text-[12px] disabled:opacity-50">
              <Radar className="size-3.5" /> Scan now
            </button>
            <p className="text-[10px] text-muted-foreground">
              The radar also scans on its own; this is for when you want an answer immediately.
            </p>
          </div>
        </Panel>
      )}

      {live.length === 0 && (
        <Panel>
          <p className="p-4 text-center text-[12px] text-muted-foreground">
            No live signals. Silence here means nothing repeated enough to notice — not that
            nothing is happening.
          </p>
        </Panel>
      )}

      {live.map((s) => (
        <Panel key={s.id}>
          <div className="space-y-1.5 p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-48 flex-1">
                <p className="text-[11px] text-muted-foreground">
                  About <b>{s.subjectName ?? 'an unnamed subject'}</b> · first seen{' '}
                  {fmtRelative(s.firstSeenAt)} · last {fmtRelative(s.lastSeenAt)}
                </p>
                <blockquote className="mt-1 border-l-2 border-amber-500/50 pl-2
                                       text-[12px] italic">
                  {s.phrase}
                </blockquote>
              </div>
              <div className="w-40">
                <LabeledBar label="Confidence" value={s.confidence} max={100} />
                <p className="text-[10px] text-muted-foreground">
                  Never reaches 100. Identical text is evidence of repetition, not of intent.
                </p>
              </div>
            </div>

            <p className="text-[11px]">
              <b>{s.distinctAuthors}</b> different accounts across <b>{s.threadCount}</b>{' '}
              thread{s.threadCount === 1 ? '' : 's'}, inside {s.windowMinutes} minutes.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <Link to="/war-room/threads" className="text-[11px] text-primary underline">
                See the threads
              </Link>
              {canTriage && (
                <button type="button" disabled={busy}
                        onClick={() => {
                          const reason = window.prompt(
                            'Why is this not coordination? (10 characters minimum — it is what '
                            + 'stops it being re-flagged next week)')
                          if (reason) void run(() => standDownSignal(s.id, reason),
                            'Stood down.')
                        }}
                        className="rounded border border-border px-2 py-0.5 text-[11px]">
                  Stand it down
                </button>
              )}
              <span className="text-[10px] text-muted-foreground">
                The answer to a repeated phrase is an ASTROTURF_INOCULATION draft — point at
                the repetition, never at the people.
              </span>
            </div>
          </div>
        </Panel>
      ))}

      <OffsetPager page={page} totalPages={total.pages} totalElements={total.elements}
                   onPage={setPage} loading={false} unit="signals" />

      {closed.length > 0 && (
        <Panel>
          <div className="p-3">
            <p className="text-[11px] font-medium">Stood down</p>
            <ul className="mt-1 space-y-1">
              {closed.map((s) => (
                <li key={s.id} className="text-[10px] text-muted-foreground">
                  “{s.phrase.slice(0, 90)}{s.phrase.length > 90 ? '…' : ''}” —{' '}
                  {s.stoodDownReason} ({fmtRelative(s.stoodDownAt)})
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      )}
    </div>
  )
}

/**
 * Search and paging for one of the war room's unbounded lists.
 *
 * <p>Threads, the approval queue, the ledger and coordination signals all accumulate for the
 * life of a campaign. The bounded lists — accounts, voices, watches, platforms — are
 * deliberately NOT paged: a room has a handful of each, and a pager that never has anything to
 * do is a control that teaches people to ignore controls.</p>
 */
function SearchBar({
  draft, onDraft, query, onSearch, onClear, total, unit,
}: {
  draft: string
  onDraft: (v: string) => void
  query: string
  onSearch: () => void
  onClear: () => void
  total: number
  unit: string
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input value={draft} onChange={(e) => onDraft(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter') onSearch() }}
             placeholder="Search the repeated phrase…"
             className="h-7 w-full max-w-xs rounded border border-input bg-background px-2
                        text-[12px]" />
      <button type="button" onClick={onSearch}
              className="rounded border border-border px-2 py-1 text-[11px]">Search</button>
      {query && (
        <button type="button" onClick={onClear}
                className="rounded border border-border px-2 py-1 text-[11px]">Clear</button>
      )}
      <span className="text-[10px] text-muted-foreground">
        {total} {unit}
      </span>
    </div>
  )
}
